import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { formatApiErrorDetail, getSessionId, isFavorite, toggleFavorite } from "@/lib/api";
import { toast } from "sonner";
import ShopHeader from "@/components/app/ShopHeader";
import SareeCard from "@/components/app/SareeCard";
import { Heart, Share2, Sparkles, ShoppingBag, Calendar, MessageCircle, MessageSquare, ChevronLeft, ChevronRight, Coins } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { isJewellery, formatInr } from "@/lib/productTypes";

export default function SareeDetail() {
    const { slug, id } = useParams();
    const nav = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeImg, setActiveImg] = useState(0);
    const [fav, setFav] = useState(false);
    const [trialOpen, setTrialOpen] = useState(false);
    const [staffOpen, setStaffOpen] = useState(false);

    useEffect(() => {
        setLoading(true);
        api.get(`/sarees/${id}`).then(({ data }) => {
            setData(data);
            setFav(isFavorite(data.saree.id));
        }).catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Not found"))
            .finally(() => setLoading(false));
    }, [id]);

    if (loading || !data) {
        return (
            <div className="min-h-screen bg-brand-beige">
                <ShopHeader slug={slug} back />
                <div className="max-w-5xl mx-auto px-4 pt-6">
                    <div className="aspect-[3/4] skel rounded-lg" />
                </div>
            </div>
        );
    }

    const { saree, similar, shop } = data;
    const allImages = (saree.ai_model_images && saree.ai_model_images.length)
        ? saree.ai_model_images
        : (saree.images || []);
    const main = allImages[activeImg] || allImages[0];

    const onFav = () => { toggleFavorite({ ...saree, shop_slug: slug }); setFav(!fav); };

    const addToCart = async () => {
        const sid = getSessionId();
        try {
            await api.post("/cart/add", { session_id: sid, saree_id: saree.id, qty: 1 });
            toast.success("Added to cart");
        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed"); }
    };

    const shareUrl = `${window.location.origin}/shop/${slug}/saree/${saree.id}`;
    const sharePrice = Number(saree.price) > 0 ? Number(saree.price) : Number(saree.auto_price || 0);
    const shareText = `Look at this beautiful ${saree.purity ? saree.purity + " piece" : "saree"} from ${shop?.name}: ${saree.title}${sharePrice > 0 ? ` — ₹${sharePrice.toLocaleString("en-IN")}` : ""} ${shareUrl}`;
    const whatsapp = () => {
        const url = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
        window.open(url, "_blank");
    };

    return (
        <div className="min-h-screen bg-brand-beige pb-32">
            <ShopHeader shop={shop} slug={slug} back />

            <section className="max-w-5xl mx-auto sm:px-6 grid lg:grid-cols-2 gap-8 pt-3 sm:pt-6">
                <div>
                    <div className="aspect-[3/4] bg-brand-subtle relative overflow-hidden sm:rounded-xl border-y sm:border border-brand-border" data-testid="saree-main-image">
                        {main ? <img src={main} alt={saree.title} loading="eager" decoding="async" fetchpriority="high" className="w-full h-full object-cover" /> : null}
                        {allImages.length > 1 ? (
                            <>
                                <button
                                    onClick={() => setActiveImg((i) => (i - 1 + allImages.length) % allImages.length)}
                                    className="absolute left-3 top-1/2 -translate-y-1/2 bg-white/90 rounded-full p-2 hover:bg-white"
                                    aria-label="prev"
                                >
                                    <ChevronLeft className="w-4 h-4" strokeWidth={1.5} />
                                </button>
                                <button
                                    onClick={() => setActiveImg((i) => (i + 1) % allImages.length)}
                                    className="absolute right-3 top-1/2 -translate-y-1/2 bg-white/90 rounded-full p-2 hover:bg-white"
                                    aria-label="next"
                                >
                                    <ChevronRight className="w-4 h-4" strokeWidth={1.5} />
                                </button>
                            </>
                        ) : null}
                        {activeImg >= 0 && saree.ai_model_images?.length ? (
                            <span className="absolute top-3 left-3 bg-brand-primary text-white text-[10px] px-2 py-1 rounded uppercase tracking-wider flex items-center gap-1">
                                <Sparkles className="w-3 h-3" strokeWidth={1.5} /> AI on model
                            </span>
                        ) : null}
                    </div>
                    {allImages.length > 1 ? (
                        <div className="px-4 sm:px-0 mt-3 flex gap-2 overflow-x-auto scroll-x">
                            {allImages.map((src, i) => (
                                <button
                                    key={i}
                                    onClick={() => setActiveImg(i)}
                                    className={`relative aspect-[3/4] w-16 rounded-md overflow-hidden border ${i === activeImg ? "border-brand-primary" : "border-brand-border"}`}
                                >
                                    <img src={src} alt="" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                                </button>
                            ))}
                        </div>
                    ) : null}
                </div>

                <div className="px-4 sm:px-0">
                    <div className="ayraa-overline">{saree.purity || saree.brand || saree.fabric}</div>
                    <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-4 leading-[1.05] tracking-editorial text-balance">{saree.title}</h1>
                    {(() => {
                        const numericPrice = Number(saree.price) > 0 ? Number(saree.price) : Number(saree.auto_price || 0);
                        const isAuto = !Number(saree.price) && Number(saree.auto_price) > 0;
                        return (
                            <div className="mt-5 flex items-baseline gap-3 flex-wrap">
                                {numericPrice > 0 ? (
                                    <>
                                        <span className="font-mono text-2xl sm:text-3xl text-brand-ink font-medium">₹{numericPrice.toLocaleString("en-IN")}</span>
                                        {isAuto ? (
                                            <span className="font-mono text-[10px] uppercase tracking-wider-2 bg-brand-bronze text-white px-2.5 py-1 inline-flex items-center gap-1"><Coins className="w-3 h-3" strokeWidth={1.5}/> Today&apos;s rate</span>
                                        ) : null}
                                        {saree.original_price && saree.original_price > numericPrice ? (
                                            <span className="font-mono text-base text-brand-soft line-through">₹{Number(saree.original_price).toLocaleString("en-IN")}</span>
                                        ) : null}
                                        {saree.offer ? (
                                            <span className="font-mono text-[10px] uppercase tracking-wider-2 bg-brand-ink text-white px-2.5 py-1">{saree.offer}</span>
                                        ) : null}
                                    </>
                                ) : (
                                    <span className="font-display text-2xl text-brand-muted">Price on request</span>
                                )}
                            </div>
                        );
                    })()}

                    <div className="my-7 h-px bg-brand-border" />

                    {isJewellery(saree.product_type) ? (
                        <>
                            <div className="grid grid-cols-2 gap-x-6 gap-y-5 text-sm" data-testid="jewellery-details">
                                {saree.purity ? <Detail k="Purity" v={saree.purity} /> : null}
                                {saree.gross_weight_g ? <Detail k="Gross weight" v={`${saree.gross_weight_g} g`} /> : null}
                                {saree.net_weight_g ? <Detail k="Net (gold)" v={`${saree.net_weight_g} g`} /> : null}
                                {saree.stone_weight_g ? <Detail k="Stone weight" v={`${saree.stone_weight_g} g`} /> : null}
                                {saree.making_charges_pct ? <Detail k="Making" v={`${saree.making_charges_pct}%`} /> : null}
                                {saree.making_charges_inr ? <Detail k="Making (flat)" v={formatInr(saree.making_charges_inr)} /> : null}
                                {saree.huid ? <Detail k="HUID / BIS" v={saree.huid} /> : null}
                                <Detail k="Stock" v={saree.stock > 0 ? `${saree.stock} available` : "Sold out"} />
                            </div>
                            {Number(saree.auto_price) > 0 && Number(saree.net_weight_g) > 0 ? (
                                <p className="mt-4 text-[11px] font-mono uppercase tracking-wider-2 text-brand-soft">
                                    Price = today&apos;s rate × {saree.net_weight_g}g{saree.making_charges_pct ? ` + ${saree.making_charges_pct}% making` : ""}{saree.making_charges_inr ? ` + ${formatInr(saree.making_charges_inr)} making` : ""}
                                </p>
                            ) : null}
                        </>
                    ) : (
                        <div className="grid grid-cols-2 gap-x-6 gap-y-5 text-sm">
                            <Detail k="Fabric" v={saree.fabric} />
                            <Detail k="Occasion" v={saree.occasion} cap />
                            <Detail k="Stock" v={saree.stock > 0 ? `${saree.stock} available` : "Sold out"} />
                            <Detail k="Colors" v={(saree.colors || []).join(", ") || "—"} />
                        </div>
                    )}

                    {saree.description ? (
                        <p className="mt-7 text-brand-muted leading-relaxed text-[15px] text-pretty">{saree.description}</p>
                    ) : null}

                    {saree.matching_blouse && !isJewellery(saree.product_type) ? (
                        <div className="mt-7 border border-brand-border bg-brand-paper p-5 rounded-sm">
                            <div className="ayraa-overline">Matching blouse</div>
                            <div className="mt-2 font-display text-lg tracking-editorial">{saree.matching_blouse}</div>
                        </div>
                    ) : null}

                    {saree.ai_model_images?.length > 1 ? (
                        <div className="mt-8">
                            <div className="ayraa-overline flex items-center gap-1.5"><Sparkles className="w-3 h-3" strokeWidth={1.5} /> More looks</div>
                            <div className="mt-4 flex gap-3 overflow-x-auto scroll-x">
                                {saree.ai_model_images.map((src, i) => (
                                    <button key={i} onClick={() => setActiveImg(i)} className={`relative aspect-square w-28 rounded-sm overflow-hidden border-2 transition flex-shrink-0 ${i === activeImg ? "border-brand-ink" : "border-transparent hover:border-brand-border"}`}>
                                        <img src={src} alt="ai-model" loading="lazy" decoding="async" className="w-full h-full object-cover" />
                                    </button>
                                ))}
                            </div>
                        </div>
                    ) : null}
                </div>
            </section>

            {similar?.length ? (
                <section className="max-w-6xl mx-auto px-5 sm:px-8 mt-20 mb-8">
                    <div className="text-center">
                        <div className="editorial-eyebrow inline-flex items-center gap-3">
                            <span className="w-6 h-px bg-brand-bronze" /> You may also love <span className="w-6 h-px bg-brand-bronze" />
                        </div>
                        <h2 className="editorial-h2 mt-5">Similar pieces</h2>
                    </div>
                    <div className="mt-10 grid grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-10">
                        {similar.slice(0, 6).map((s) => <SareeCard key={s.id} saree={s} shopSlug={slug} />)}
                    </div>
                </section>
            ) : null}

            <div className="fixed bottom-0 left-0 right-0 glass-dock border-t border-brand-border p-3 z-40 shadow-glass-up">
                <div className="max-w-6xl mx-auto flex items-center gap-2">
                    <button onClick={onFav} className={`w-12 h-12 grid place-items-center border rounded-sm transition ${fav ? "bg-brand-ink text-white border-brand-ink" : "border-brand-border bg-white hover:border-brand-ink"}`} aria-label="favorite" data-testid="fav-toggle">
                        <Heart className={`w-4 h-4 ${fav ? "fill-current" : ""}`} strokeWidth={1.5} />
                    </button>
                    <button onClick={whatsapp} className="w-12 h-12 grid place-items-center border border-brand-border bg-white rounded-sm hover:border-brand-ink transition" aria-label="share" data-testid="whatsapp-share-btn">
                        <Share2 className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                    <Dialog open={staffOpen} onOpenChange={setStaffOpen}>
                        <DialogTrigger asChild>
                            <button className="w-12 h-12 grid place-items-center border border-brand-border bg-white rounded-sm hover:border-brand-ink transition" aria-label="staff" data-testid="staff-request-btn">
                                <MessageSquare className="w-4 h-4" strokeWidth={1.5} />
                            </button>
                        </DialogTrigger>
                        <StaffDialogContent shopSlug={slug} sareeId={saree.id} onDone={() => setStaffOpen(false)} />
                    </Dialog>
                    <Dialog open={trialOpen} onOpenChange={setTrialOpen}>
                        <DialogTrigger asChild>
                            <button className="ayraa-btn-outline flex-1 py-3 text-xs uppercase tracking-wider-2 font-mono" data-testid="book-trial-btn">
                                <Calendar className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Book trial
                            </button>
                        </DialogTrigger>
                        <TrialDialogContent shopSlug={slug} sareeId={saree.id} onDone={() => setTrialOpen(false)} />
                    </Dialog>
                    <button onClick={addToCart} className="ayraa-btn-primary flex-[1.4] py-3 text-xs uppercase tracking-wider-2 font-mono" data-testid="add-cart-btn">
                        <ShoppingBag className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Add to cart
                    </button>
                </div>
            </div>
        </div>
    );
}

function Detail({ k, v, cap }) {
    const text = cap && typeof v === "string" ? v.charAt(0).toUpperCase() + v.slice(1) : v;
    return (
        <div>
            <div className="ayraa-overline">{k}</div>
            <div className="mt-2 text-brand-ink text-[15px]">{text || "—"}</div>
        </div>
    );
}

function TrialDialogContent({ shopSlug, sareeId, onDone }) {
    const [f, setF] = useState({ customer_name: "", customer_phone: "", preferred_date: "", note: "" });
    const [busy, setBusy] = useState(false);
    const submit = async (e) => {
        e.preventDefault(); setBusy(true);
        try {
            await api.post("/trial-bookings", { ...f, shop_slug: shopSlug, saree_id: sareeId });
            toast.success("Trial booked! The shop will reach out shortly."); onDone();
        } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    return (
        <DialogContent className="bg-brand-paper border-brand-border">
            <DialogHeader><DialogTitle className="font-serif text-2xl">Book a trial</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3" data-testid="trial-form">
                <input className="ayraa-input" placeholder="Your name" required value={f.customer_name} onChange={(e) => setF({ ...f, customer_name: e.target.value })} data-testid="trial-name" />
                <input className="ayraa-input" placeholder="Phone (with country code)" required value={f.customer_phone} onChange={(e) => setF({ ...f, customer_phone: e.target.value })} data-testid="trial-phone" />
                <input className="ayraa-input" type="date" required value={f.preferred_date} onChange={(e) => setF({ ...f, preferred_date: e.target.value })} data-testid="trial-date" />
                <textarea className="ayraa-input min-h-[70px]" placeholder="Any preferences? (optional)" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} />
                <DialogFooter><button className="ayraa-btn-primary w-full" disabled={busy} data-testid="trial-submit">{busy ? "Booking…" : "Confirm trial"}</button></DialogFooter>
            </form>
        </DialogContent>
    );
}

function StaffDialogContent({ shopSlug, sareeId, onDone }) {
    const [f, setF] = useState({ customer_name: "", customer_phone: "", message: "" });
    const [busy, setBusy] = useState(false);
    const submit = async (e) => {
        e.preventDefault(); setBusy(true);
        try {
            await api.post("/staff-requests", { ...f, shop_slug: shopSlug, saree_id: sareeId });
            toast.success("Staff has been notified. They'll be with you shortly."); onDone();
        } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
        finally { setBusy(false); }
    };
    return (
        <DialogContent className="bg-brand-paper border-brand-border">
            <DialogHeader><DialogTitle className="font-serif text-2xl">Request staff</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3" data-testid="staff-form">
                <input className="ayraa-input" placeholder="Your name" required value={f.customer_name} onChange={(e) => setF({ ...f, customer_name: e.target.value })} />
                <input className="ayraa-input" placeholder="Phone (with country code)" required value={f.customer_phone} onChange={(e) => setF({ ...f, customer_phone: e.target.value })} />
                <textarea className="ayraa-input min-h-[70px]" placeholder="What would you like help with?" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} />
                <DialogFooter><button className="ayraa-btn-primary w-full" disabled={busy} data-testid="staff-submit">{busy ? "Sending…" : "Notify staff"}</button></DialogFooter>
            </form>
        </DialogContent>
    );
}
